import React, { useEffect, useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useAlert } from './contexts/AlertContext';
import apiService from './apiService';
import { FileText, Download, Check, Clock, XCircle } from 'lucide-react';

const MyInvoices = () => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        loadInvoices();
    }, [filter]);

    const loadInvoices = async () => {
        try {
            const status = filter === 'all' ? null : filter;
            const data = await apiService.getInvoices({ status });
            setInvoices(data);
        } catch (e) {
            showAlert('Failed to load invoices', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-700',
            paid: 'bg-green-100 text-green-700',
            overdue: 'bg-red-100 text-red-700',
            cancelled: 'bg-gray-100 text-gray-700'
        };
        const icons = {
            pending: Clock,
            paid: Check,
            overdue: XCircle,
            cancelled: XCircle
        };
        const Icon = icons[status] || Clock;
        return (
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${styles[status] || 'bg-gray-100'}`}>
                <Icon className="w-4 h-4" />
                {status}
            </span>
        );
    };

    if (!['org_admin', 'cashier'].includes(currentUser?.role)) {
        return <div className="p-6 text-red-600">Access denied.</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold">My Invoices</h2>
                    <p className="text-gray-600 dark:text-gray-400">View your organization's invoices (Read-only)</p>
                </div>
                
                <div className="flex gap-2">
                    {['all', 'pending', 'paid', 'overdue'].map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-2 rounded-lg capitalize ${
                                filter === f 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center">Loading invoices...</div>
                ) : invoices.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No invoices found</p>
                    </div>
                ) : (
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                            <tr>
                                <th className="p-3">Invoice #</th>
                                <th className="p-3">Date</th>
                                <th className="p-3">Due Date</th>
                                <th className="p-3">Amount</th>
                                <th className="p-3">Status</th>
                                <th className="p-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map(inv => (
                                <tr key={inv.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="p-3 font-semibold">{inv.invoiceNumber}</td>
                                    <td className="p-3">{new Date(inv.invoiceDate).toLocaleDateString()}</td>
                                    <td className="p-3">{new Date(inv.dueDate).toLocaleDateString()}</td>
                                    <td className="p-3 font-semibold">${inv.totalAmount?.toLocaleString()}</td>
                                    <td className="p-3">{getStatusBadge(inv.status)}</td>
                                    <td className="p-3">
                                        <button className="text-blue-600 hover:underline flex items-center gap-1">
                                            <Download className="w-4 h-4" />
                                            Download
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Pending</p>
                    <p className="text-2xl font-bold text-yellow-600">
                        ${invoices.filter(i => i.status === 'pending').reduce((sum, i) => sum + i.totalAmount, 0).toLocaleString()}
                    </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Paid</p>
                    <p className="text-2xl font-bold text-green-600">
                        ${invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.paidAmount, 0).toLocaleString()}
                    </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Overdue</p>
                    <p className="text-2xl font-bold text-red-600">
                        ${invoices.filter(i => i.status === 'overdue').reduce((sum, i) => sum + i.totalAmount, 0).toLocaleString()}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MyInvoices;

